import { TypedDocumentNode, gql } from '@apollo/client';
import { CSSProperties } from 'react';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { Banner_config } from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ gap: 1.5 })`
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  background: var(--c-nl-500);
  border-radius: var(--double-unit);
  backdrop-filter: blur(var(--double-unit));

  @media ${laptopAndAbove} {
    padding: var(--double-unit) var(--triple-unit);
  }
`;
const TextWrapper = styled(Vertical)`
  flex: 1;
  gap: var(--half-unit);
`;
const DesktopAndAboveWrapper = styled.div`
  display: none;

  @media ${desktopAndAbove} {
    display: block;
  }
`;
const ImgWrapper = styled(Horizontal).attrs({ center: true })`
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.8;
    background: var(--blur-color);
    filter: blur(var(--double-unit));
    z-index: -1;
  }
`;
const Img = styled.img`
  width: calc(7 * var(--unit));
  object-fit: contain;
`;

type Props = { config: Banner_config };
export const Banner = ({ config }: Props) => {
  const { lobbyTile } = config;
  const { desktopBgImageUrl, description, ctaLabel, borderColor, title } =
    lobbyTile || {};
  return (
    <Root>
      {desktopBgImageUrl && (
        <ImgWrapper style={{ '--blur-color': borderColor } as CSSProperties}>
          <Img src={desktopBgImageUrl} alt="" />
        </ImgWrapper>
      )}
      <TextWrapper>
        <LabelL bold>{title}</LabelL>
        <LabelM color="var(--c-nd-600)">{description}</LabelM>
      </TextWrapper>
      <DesktopAndAboveWrapper>
        <Button size="small" color="tertiary">
          {ctaLabel}
        </Button>
      </DesktopAndAboveWrapper>
    </Root>
  );
};

Banner.fragments = {
  config: gql`
    fragment Banner_config on Config {
      id
      lobbyTile(sport: FOOTBALL, position: BANNER) {
        id
        desktopBgImageUrl
        description
        ctaLabel
        borderColor
        title
      }
    }
  ` as TypedDocumentNode<Banner_config>,
};
